import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Search, Radar, BadgeCheck, MapPin, Play, Users, Building2 } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useDebounce } from '../hooks/useDebounce';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Filter options
const POSITIONS = ['All', 'FW', 'MID', 'DEF', 'GK'];
const LEVELS = ['All', 'Amateur', 'Semi-Pro', 'Pro'];
const REGIONS = ['All', 'Riyadh', 'Jeddah', 'Dammam', 'Mecca', 'Medina'];
const PREFERRED_FOOT = ['All', 'Right', 'Left', 'Both'];

// Position badge colors
const POSITION_COLORS = {
    'FW': 'bg-red-500',
    'ST': 'bg-red-500',
    'CF': 'bg-red-500',
    'LW': 'bg-red-500',
    'RW': 'bg-red-500',
    'CAM': 'bg-orange-500',
    'MID': 'bg-orange-500',
    'CM': 'bg-orange-500',
    'CDM': 'bg-yellow-600',
    'DEF': 'bg-blue-500',
    'CB': 'bg-blue-500',
    'LB': 'bg-blue-500',
    'RB': 'bg-blue-500',
    'GK': 'bg-green-600',
};

// Filter Pill Component
const FilterPill = ({ label, active, onClick }) => (
    <button
        onClick={onClick}
        className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
            active 
                ? 'bg-primary text-white' 
                : 'bg-surface border border-border text-white/70 hover:border-primary/50'
        }`}
    >
        {label}
    </button>
);

// Player Card Component
const PlayerCard = ({ player, onClick }) => {
    const positionShort = player.position?.split(' ')[0]?.toUpperCase() || 'MID';
    const positionColor = POSITION_COLORS[positionShort] || POSITION_COLORS['MID'];
    
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card-bg border border-border rounded-xl overflow-hidden"
            data-testid={`player-card-${player.id}`}
        >
            {/* Player Image/Avatar */}
            <div className="relative aspect-square bg-gradient-to-b from-surface to-black">
                <img 
                    src={player.avatar}
                    alt={player.username}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${player.username}&background=169239&color=fff&size=200`;
                    }}
                />
                {/* Position Badge */}
                <div className={`absolute top-2 right-2 ${positionColor} px-2 py-0.5 rounded text-xs font-bold text-white`}>
                    {positionShort}
                </div>
                {/* Verified Badge */}
                {player.is_verified && (
                    <div className="absolute top-2 left-2 bg-black/50 backdrop-blur-sm rounded-full p-1">
                        <BadgeCheck className="w-4 h-4 text-primary fill-primary" />
                    </div>
                )}
                {/* Level Badge */}
                {player.level && (
                    <div className="absolute bottom-2 left-2 bg-black/70 backdrop-blur-sm px-2 py-0.5 rounded text-xs text-white/80">
                        {player.level}
                    </div>
                )}
            </div>
            
            {/* Player Info */}
            <div className="p-3">
                <h3 className="text-white font-semibold text-sm truncate">{player.username}</h3>
                {player.region && (
                    <div className="flex items-center gap-1 text-white/50 text-xs mt-1">
                        <MapPin className="w-3 h-3" />
                        <span>{player.region}</span>
                    </div>
                )}
                
                {/* Watch Highlights Button */}
                <Button
                    onClick={() => onClick(player)}
                    size="sm"
                    className="w-full mt-3 bg-primary/20 hover:bg-primary/30 text-primary border border-primary/30 rounded-lg text-xs font-medium"
                    data-testid={`watch-highlights-${player.id}`}
                >
                    <Play className="w-3 h-3 mr-1" />
                    Watch Highlights
                </Button>
            </div>
        </motion.div>
    );
};

// User Row Component (for Find Fans/Clubs tab)
const UserRow = ({ user, onClick }) => (
    <motion.button
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => onClick(user)}
        className="w-full flex items-center gap-3 p-3 bg-surface border border-border rounded-xl hover:bg-white/5 transition-colors"
        data-testid={`user-row-${user.id}`}
    >
        <Avatar className="w-12 h-12 border border-border">
            <AvatarImage src={user.avatar} alt={user.username} />
            <AvatarFallback className="bg-primary text-white">
                {user.username?.charAt(0).toUpperCase()}
            </AvatarFallback>
        </Avatar>
        <div className="flex-1 text-left">
            <div className="flex items-center gap-1">
                <span className="text-white font-medium">{user.username}</span>
                {user.is_verified && (
                    <BadgeCheck className="w-4 h-4 text-primary fill-primary" />
                )}
            </div>
            <div className="flex items-center gap-2 text-white/50 text-xs">
                {user.role && <span className="capitalize">{user.role}</span>}
                {user.region && <span>• {user.region}</span>}
            </div>
        </div>
        <div className="text-white/40 text-xs">
            {user.followers_count?.toLocaleString()} followers
        </div>
    </motion.button>
);

const ScoutRadarPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('players');
    const [players, setPlayers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Filters state
    const [selectedPosition, setSelectedPosition] = useState('All');
    const [selectedLevel, setSelectedLevel] = useState('All');
    const [selectedRegion, setSelectedRegion] = useState('All');
    const [selectedFoot, setSelectedFoot] = useState('All');
    
    // Search state for Find Fans/Clubs tab
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearch = useDebounce(searchQuery, 300);

    useEffect(() => {
        fetchPlayers();
        fetchAllUsers();
    }, []);

    const fetchPlayers = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/scout/players`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            setPlayers(response.data);
        } catch (error) {
            console.error('Error fetching players:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllUsers = async () => {
        try {
            const response = await axios.get(`${API}/users/search?q=`);
            setAllUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    // Filter players based on selected filters
    const filteredPlayers = players.filter(player => {
        if (selectedPosition !== 'All') {
            const pos = player.position?.toUpperCase() || '';
            if (!pos.includes(selectedPosition)) return false;
        }
        if (selectedLevel !== 'All' && player.level !== selectedLevel) return false;
        if (selectedRegion !== 'All' && player.region !== selectedRegion) return false;
        if (selectedFoot !== 'All' && player.preferred_foot !== selectedFoot) return false;
        return true;
    });

    // Filter users for search
    const filteredUsers = allUsers.filter(user => {
        if (!debouncedSearch) return true;
        return user.username?.toLowerCase().includes(debouncedSearch.toLowerCase());
    });

    const handlePlayerClick = (player) => {
        navigate(`/profile/${player.id}`);
    };

    const handleUserClick = (user) => {
        navigate(`/profile/${user.id}`);
    };

    return (
        <div className="h-full flex flex-col bg-black" data-testid="scout-radar-page">
            {/* Header */}
            <div className="flex-shrink-0 glass border-b border-white/10">
                <div className="max-w-md mx-auto px-4 py-4">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                            <Radar className="w-5 h-5 text-primary" />
                        </div>
                        <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                            Scout Radar
                        </h1>
                    </div>

                    <Tabs value={activeTab} onValueChange={setActiveTab}>
                        <TabsList className="w-full bg-surface border border-border">
                            <TabsTrigger 
                                value="players" 
                                className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-white"
                                data-testid="tab-players"
                            >
                                <Users className="w-4 h-4 mr-2" />
                                Scout Players
                            </TabsTrigger>
                            <TabsTrigger 
                                value="fans" 
                                className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-white"
                                data-testid="tab-fans"
                            >
                                <Building2 className="w-4 h-4 mr-2" />
                                Find Fans/Clubs
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
                {activeTab === 'players' ? (
                    <div className="max-w-md mx-auto">
                        {/* Filter Pills */}
                        <div className="px-4 py-3 space-y-3">
                            {/* Position Filter */}
                            <div>
                                <p className="text-white/50 text-xs mb-2 uppercase tracking-wider">Position</p>
                                <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
                                    {POSITIONS.map(pos => (
                                        <FilterPill
                                            key={pos}
                                            label={pos}
                                            active={selectedPosition === pos}
                                            onClick={() => setSelectedPosition(pos)}
                                        />
                                    ))}
                                </div>
                            </div>
                            
                            {/* Level Filter */}
                            <div>
                                <p className="text-white/50 text-xs mb-2 uppercase tracking-wider">Level</p>
                                <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
                                    {LEVELS.map(level => (
                                        <FilterPill
                                            key={level}
                                            label={level}
                                            active={selectedLevel === level}
                                            onClick={() => setSelectedLevel(level)}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Region Filter */}
                            <div>
                                <p className="text-white/50 text-xs mb-2 uppercase tracking-wider">Region</p>
                                <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
                                    {REGIONS.map(region => (
                                        <FilterPill
                                            key={region}
                                            label={region}
                                            active={selectedRegion === region}
                                            onClick={() => setSelectedRegion(region)}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Preferred Foot Filter */}
                            <div>
                                <p className="text-white/50 text-xs mb-2 uppercase tracking-wider">Preferred Foot</p>
                                <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
                                    {PREFERRED_FOOT.map(foot => (
                                        <FilterPill
                                            key={foot}
                                            label={foot}
                                            active={selectedFoot === foot}
                                            onClick={() => setSelectedFoot(foot)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Results Count */}
                        <div className="px-4 py-2">
                            <p className="text-white/50 text-sm">
                                {filteredPlayers.length} player{filteredPlayers.length !== 1 ? 's' : ''} found
                            </p>
                        </div>

                        {/* Player Cards Grid */}
                        {loading ? (
                            <div className="flex justify-center py-12">
                                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : filteredPlayers.length === 0 ? (
                            <div className="py-12 text-center px-4">
                                <Radar className="w-12 h-12 text-white/20 mx-auto mb-3" />
                                <p className="text-white/60">No players match your filters</p>
                                <p className="text-white/40 text-sm mt-1">Try adjusting your search criteria</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-3 px-4 pb-24" data-testid="players-grid">
                                {filteredPlayers.map((player, index) => (
                                    <PlayerCard
                                        key={player.id}
                                        player={player}
                                        onClick={handlePlayerClick}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="max-w-md mx-auto px-4 py-4 pb-24">
                        {/* Search Bar */}
                        <div className="relative mb-4">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                            <Input
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search fans and clubs..."
                                className="w-full bg-surface border-border text-white placeholder:text-white/40 pl-10 h-12"
                                data-testid="fans-search-input"
                            />
                        </div>

                        {/* Suggested Section */}
                        <div className="mb-4">
                            <p className="text-white/50 text-xs uppercase tracking-wider mb-3">
                                {searchQuery ? 'Results' : 'Suggested for you'}
                            </p>
                        </div>

                        {/* Users List */}
                        <div className="space-y-2" data-testid="fans-list">
                            {filteredUsers.length === 0 ? (
                                <div className="py-12 text-center">
                                    <Users className="w-12 h-12 text-white/20 mx-auto mb-3" />
                                    <p className="text-white/60">No users found</p>
                                </div>
                            ) : (
                                filteredUsers.map((user, index) => (
                                    <UserRow
                                        key={user.id}
                                        user={user}
                                        onClick={handleUserClick}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ScoutRadarPage;
